import {Box, FormControl, Grid, Typography} from '@mui/material';
import Checkbox from 'components/Checkbox';
import HolidaysDatePickerController from 'controllers/HolidaysDatePickerController';
import {isEqual} from 'date-fns';
import {FC, useEffect, useState} from 'react';

export interface FormRangeDateWithHalfDay {
  startDate?: Date | null;
  startCheck?: boolean;
  endDate?: Date | null;
  endCheck?: boolean;
}

interface FormRangeDateLabels {
  startDateLabel?: string;
  endDateLabel?: string;
  startCheckLabel?: string;
  endCheckLabel?: string;
  optionalStartCheckLabel?: string;
  optionalEndCheckLabel?: string;
}

export interface LeaveDateRangePickerProps {
  onChange?: (e: FormRangeDateWithHalfDay) => void;
  labels?: FormRangeDateLabels;
  defaultValue?: FormRangeDateWithHalfDay;
  withChecks?: boolean;
  leaveType?: string;
  /** Warning message displayed below the start date picker when the selected date is within 30 days */
  earlyDateWarning?: string;
}

const LeaveDateRangePickerController: FC<LeaveDateRangePickerProps> = props => {
  const startCheckName = 'startCheck';
  const endCheckName = 'endCheck';
  const {
    onChange,
    labels,
    defaultValue,
    withChecks,
    leaveType,
    earlyDateWarning,
  } = props;

  const initialState: FormRangeDateWithHalfDay = {
    startDate: null,
    startCheck: false,
    endDate: null,
    endCheck: false,
  };
  const [form, setForm] = useState<FormRangeDateWithHalfDay>(initialState);
  const {startDate, startCheck, endDate, endCheck} = form;

  useEffect(() => {
    if (onChange) {
      onChange({
        ...form,
        ...(withChecks ? {} : {startCheck: false, endCheck: false}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, withChecks]);

  let labelStartCheck = labels?.startCheckLabel;
  let labelEndCheck = labels?.endCheckLabel;

  const isSameDay = startDate && endDate && isEqual(startDate, endDate);
  if (isSameDay) {
    labelStartCheck = labels?.optionalStartCheckLabel;
    labelEndCheck = labels?.optionalEndCheckLabel;
    if (startCheck && endCheck) {
      setForm(prevForm => ({
        ...prevForm,
        startCheck: false,
        endCheck: false,
      }));
    }
  }

  const handleSameDayCheck = (name: string, checked: boolean) => {
    if (name === startCheckName) {
      setForm(prevForm => ({
        ...prevForm,
        startCheck: checked,
        endCheck: checked ? false : prevForm.endCheck,
      }));
    } else {
      setForm(prevForm => ({
        ...prevForm,
        startCheck: checked ? false : prevForm.startCheck,
        endCheck: checked,
      }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCheckField = (e: any, checked: boolean) => {
    const {name} = e.target;
    const isSameDay = startDate && endDate && isEqual(startDate, endDate);
    if (isSameDay) {
      handleSameDayCheck(name, checked);
    } else {
      setForm(prevForm => ({...prevForm, [name]: checked}));
    }
  };

  const handleStartDateField = (date: Date | null) => {
    setForm(prevForm => ({...prevForm, startDate: date}));
  };

  const handleEndDateField = (date: Date | null) => {
    setForm(prevForm => ({...prevForm, endDate: date}));
  };

  useEffect(() => {
    defaultValue && setForm(prevForm => ({...prevForm, ...defaultValue}));
  }, [defaultValue]);

  return (
    <FormControl fullWidth>
      <Grid container>
        <Grid item className={withChecks ? '' : 'pr-2'}>
          <HolidaysDatePickerController
            label={labels?.startDateLabel || ''}
            onChangeDates={handleStartDateField}
            selectionType="single"
            maxDate={form.endDate || undefined}
            defaultValue={form.startDate || undefined}
            leaveType={leaveType}
          />
        </Grid>
        {withChecks && (
          <Grid item paddingLeft={2} paddingBottom={5}>
            <Checkbox
              className="mt-9"
              name={startCheckName}
              checked={startCheck}
              value={startCheck}
              control={<></>}
              label={labelStartCheck}
              onChange={handleCheckField}
            />
          </Grid>
        )}
      </Grid>
      {earlyDateWarning && (
        <Box>
          <Typography variant="caption" color="warning.main">
            {earlyDateWarning}
          </Typography>
        </Box>
      )}
      <Grid container>
        <Grid item className={withChecks ? '' : 'pl-2'}>
          <HolidaysDatePickerController
            label={labels?.endDateLabel || ''}
            onChangeDates={handleEndDateField}
            selectionType="single"
            minDate={form.startDate || undefined}
            defaultValue={form.endDate || undefined}
            leaveType={leaveType}
          />
        </Grid>
        {withChecks && (
          <Grid item paddingLeft={2} paddingBottom={2}>
            <Checkbox
              className="mt-9"
              name={endCheckName}
              checked={endCheck}
              value={endCheck}
              control={<></>}
              label={labelEndCheck}
              onChange={handleCheckField}
            />
          </Grid>
        )}
      </Grid>
    </FormControl>
  );
};

export default LeaveDateRangePickerController;
